import {
    TextInput,
    PasswordInput,
    NumberInput,
    Select,
    Switch,
    TagsInput,
} from '@mantine/core';
import type { ProviderConfigField, ProviderConfigFieldType } from '@bmai/shared';
import IntegrationOptionField from '../IntegrationOptionField';
import type { ProviderOption } from '../IntegrationsService';

export interface RenderFieldArgs {
    field: ProviderConfigField;
    value: unknown;
    onChange: (value: unknown) => void;
    secretPlaceholder?: string;
    onLoadOptions: (route: string) => Promise<ProviderOption[]>;
}

export function renderField({ field, value, onChange, secretPlaceholder, onLoadOptions }: RenderFieldArgs): React.ReactElement {
    const label = field.displayLabel;
    const required = field.required;
    const description = field.description;

    if (field.optionSource) {
        return (
            <IntegrationOptionField
                key={field.name}
                field={field}
                value={value}
                multi={field.fieldType === 'string-array'}
                onChange={onChange}
                onLoadOptions={onLoadOptions}
            />
        );
    }

    if (field.enumOptions) {
        return (
            <Select
                key={field.name}
                label={label}
                description={description}
                required={required}
                data={field.enumOptions}
                value={typeof value === 'string' ? value : null}
                onChange={(v) => onChange(v ?? '')}
            />
        );
    }

    switch (field.fieldType satisfies ProviderConfigFieldType) {
        case 'secret':
            return (
                <PasswordInput
                    key={field.name}
                    label={label}
                    description={description}
                    required={required}
                    placeholder={secretPlaceholder}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            );
        case 'number':
            return (
                <NumberInput
                    key={field.name}
                    label={label}
                    description={description}
                    required={required}
                    value={typeof value === 'number' ? value : undefined}
                    onChange={(v) => onChange(typeof v === 'number' ? v : Number(v) || 0)}
                />
            );
        case 'boolean':
            return (
                <Switch
                    key={field.name}
                    label={label}
                    description={description}
                    checked={value === true}
                    onChange={(e) => onChange(e.currentTarget.checked)}
                />
            );
        case 'url':
            return (
                <TextInput
                    key={field.name}
                    label={label}
                    description={description}
                    required={required}
                    type="url"
                    placeholder="https://…"
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            );
        case 'string-array':
            return (
                <TagsInput
                    key={field.name}
                    label={label}
                    description={description}
                    required={required}
                    value={Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []}
                    onChange={(v) => onChange(v)}
                    clearable
                />
            );
        case 'date':
            return (
                <TextInput
                    key={field.name}
                    label={label}
                    description={description}
                    required={required}
                    type="date"
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            );
        case 'string':
        default:
            return (
                <TextInput
                    key={field.name}
                    label={label}
                    description={description}
                    required={required}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            );
    }
}
